import * as React from 'react';
import Footer from '../../main-layout/footer/Footer';
import Header from '../../main-layout/header/Header';
import './MainWrapper.scss';
import { observer } from 'mobx-react';
import { buildModal } from './modal/ModalBuilder';
import { RequestsI, LightRequest } from '../../types';
import { lazy, useEffect, useState } from 'react';
import { useLocalStorage } from 'react-use';
import { NewsModal } from './NewsModal';
import Banner from '../../components/banner/Banner';
import { SystemStore } from '../../store/System';
import { AccessStore } from '../../store/Access';
import { useLocation } from 'react-router';
import TechnicalWorksPage from '../../components/banner/TechinicalWorksPage';
import NoPermissionPage from '../../components/banner/NoPermissionPage';
import { HelpDesk, HelpDeskProvider } from '@iba-team/help-desk/dist/esm/index';
import { FormFields } from '../../types';
import { blobCreator } from '../../utils/functions';
import { SupportStore } from '../../store/Support';
import { ProjectManagementStore } from '../../store/ProjectManagement';
import useToken from '../../root/useToken';
import { SermoAIProvider, Theme } from '@iba-team/sermoai/dist';
import '@iba-team/sermoai/dist/assets/output.css';

const RequestDetailsModal = lazy(() => import ('../../components/requests/modals/RequestDetailsModal'));
const CreateRequestFromPlannedModal = lazy(() => import ('../../components/requestModal/CreateRequestFromPlanned'));
const RequestModal = lazy(() => import ('../../components/requestModal/RequestModal'));
const ApproveRequestModal = lazy(() => import ('../../components/requests/modals/ApproveRequestModal'));
const RejectRequestModal = lazy(() => import ('../../components/requests/modals/RejectRequestModal'));
const CancelRequestModal = lazy(() => import ('../../components/requests/modals/CancelRequestModal'));
const EditRequestModal = lazy(()=> import('../../components/requestModal/EditRequestModal'));
const EditRequestForEmployeeModal = lazy(()=> import('../../components/requestModal/EditRequestForEmployeeModal'));
const HistoryModal = lazy(()=> import('../../components/daysAvailable/settings/modals/HistoryModal'));

interface RequestsWithYear extends Partial<RequestsI> {
  selectedYear?: number,
}

export const requestModalCall =
  buildModal<undefined, RequestsWithYear>((item,close)=> <RequestModal onClose={close} selectedYear={item?.selectedYear}/>);
export const updateModalCall =
  buildModal<undefined, {selectedYear?: number, currentItemId: number}>((item,close) =>
    <EditRequestModal currentItemId={item.currentItemId} onClose={close} selectedYear={item?.selectedYear}/>);
export const approveRequestModalCall =
  buildModal<undefined, LightRequest>((currentItem,close) =>
    <ApproveRequestModal currentItem={currentItem} onClose={close}/> );
export const requestDetailsModalCall =
  buildModal<undefined, Partial<RequestsWithYear>>((currentItem,close) =>
    <RequestDetailsModal currentItem={currentItem} onClose={close} selectedYear={currentItem.selectedYear}/>);
export const rejectRequestModalCall =
  buildModal<undefined, Partial<RequestsI>>((currentItem,close) =>
    <RejectRequestModal currentItem={currentItem} onClose={close}/>);

export const requestFromPlannedCall =
  buildModal<undefined, {selectedYear?: number, startDate: string, endDate: string}>((item,close) =>
    <CreateRequestFromPlannedModal  onClose={close} selectedYear={item.selectedYear} startDate={item.startDate} endDate={item.endDate}/>);
export const cancelRequestModalCall =
  buildModal<undefined, Partial<RequestsWithYear>>((currentItem, close) =>
    <CancelRequestModal currentItem={currentItem} onClose={close} selectedYear={currentItem.selectedYear}/>);
export const updateForEmployeeModalCall =
  buildModal<undefined, {selectedYear?: number, currentItemId: number}>((selected, close) =>
    <EditRequestForEmployeeModal onClose={close} selectedYear={selected.selectedYear} currentItemId={selected.currentItemId}/>);
export const historyModalCall =
  buildModal<undefined, number>((userId, close) =>
    <HistoryModal onClose={close} userId={userId}/>);

interface MainWrapperI {
  children: React.ReactNode,
  decodedUserEmail: string,
}

const MainWrapper = observer((props: MainWrapperI) =>{
  const {sendFeedback} = SupportStore;
  const {isNecessaryToShowMessage, isNecessaryToBlock, startDateTime} = SystemStore.systemState;
  const {checkActualityNewsModal, isShowModal} = SystemStore;
  const {children} = props;
  const [showNewsModal, setShowNewsModal] = useState(false);
  const [isShowBanner, deleteShowBanner] = useLocalStorage('isNecessaryToShowMessage',isNecessaryToShowMessage);
  const {fetchMenuItems, noPermission, isAllAccessesFetched} = AccessStore;
  const location = useLocation();
  const { fetchSystemState } = SystemStore;
  const { featureFlags, featureSelectors } = ProjectManagementStore;
  const {token} = useToken();
  const clearStateBanner = () =>{
    deleteShowBanner(false);
  };


  useEffect(()=>{
    fetchMenuItems();
  },[]);

  useEffect(()=>{
    fetchSystemState();
  },[location.pathname]);

  useEffect(()=>{
    if (isNecessaryToBlock) {
      localStorage.removeItem('isNecessaryToShowMessage');
      localStorage.removeItem('isNecessaryToBlock');
    }
  },[isNecessaryToBlock]);

  useEffect(()=>{
    deleteShowBanner(isNecessaryToShowMessage);
  },[isNecessaryToShowMessage]);

  requestModalCall.useIt();
  updateModalCall.useIt();
  requestFromPlannedCall.useIt();
  approveRequestModalCall.useIt();
  requestDetailsModalCall.useIt();
  rejectRequestModalCall.useIt();
  cancelRequestModalCall.useIt();
  updateForEmployeeModalCall.useIt();
  historyModalCall.useIt();

  useEffect(()=> {
    if(isShowModal !== null && !isShowModal) {
      setShowNewsModal(true);
    }
    if(isShowModal === null) {
      checkActualityNewsModal();
    }
  },[isShowModal]);

  const submit = (fields: Partial<FormFields>, res: {data: string | null})=> {
    const formData = new FormData();
    const blob = blobCreator({title: fields.title, message: fields.description, trace: `${res.data}`});
    formData.append('supportRequest', blob,'supportRequest.json');
    if (fields?.attachments?.length) {
      fields.attachments.forEach((item)=>{
        formData.append('attachments', item);
      });
    }
    sendFeedback(formData).then();
  };


  return (
    <div>
      {isAllAccessesFetched && !noPermission && showNewsModal && <NewsModal onClose={()=> setShowNewsModal(false)}/>}
      {isShowBanner && isNecessaryToShowMessage && startDateTime && <Banner clearStateBanner={clearStateBanner}/>}
      <>
        <Header limitedAccess={noPermission}/>
        <div className={'pagesWrapper'}>
          {isNecessaryToBlock ?
            <TechnicalWorksPage/>
            :
            noPermission ?
              <NoPermissionPage/>
              :
              <>
                {children}
              </>
          }
        </div>
        <Footer/>
      </>
      {!noPermission && token &&
        <HelpDeskProvider theme={'green'}>
          <HelpDesk token={token} customOnSubmit={submit} application={process.env.REACT_APP_LM_APPLICATION_ID || '051D.0'}/>
        </HelpDeskProvider>
      }
      {(   featureFlags.SHOW_AI_ASSISTANT
        || featureSelectors.SHOW_AI_ASSISTANT_FOR_6_DEP.summaryValue
        || featureSelectors.SHOW_AI_ASSISTANT_FOR_GDO.summaryValue) && <SermoAIProvider
        mode={'ws'}
        api_prefix_url={process.env.REACT_APP_SERMO_AI_URL || ''}
        token={token} theme={Theme.BLUE}
      />
      }
    </div>
  );
});
export default MainWrapper;
